import React from 'react';
import type { PdfDocAttachment } from '~/lib/pdf/pdf-text';
import { classNames } from '~/utils/classNames';

interface PdfAttachmentPreviewProps {
  attachment: PdfDocAttachment | null;
  onRemove: () => void;
}

function statusLine(attachment: PdfDocAttachment): { text: string; tone: 'muted' | 'accent' | 'danger' } {
  switch (attachment.status) {
    case 'processing':
      return { text: 'Reading text…', tone: 'accent' };
    case 'ready': {
      const pages = attachment.result?.pageCount ?? 0;
      const suffix = attachment.result?.truncated ? ', truncated' : '';

      return { text: `Text attached — ${pages} page${pages === 1 ? '' : 's'}${suffix}`, tone: 'muted' };
    }
    case 'error':
    default:
      return { text: attachment.error ?? "Couldn't read this PDF", tone: 'danger' };
  }
}

/**
 * Composer chip for a PDF attached as reference text (not a brochure). Mirrors
 * BrochurePreview's compact layout but is deliberately plain — no "building your
 * site" framing, since this file is just context for the conversation (DEV-142).
 */
const PdfAttachmentPreview: React.FC<PdfAttachmentPreviewProps> = ({ attachment, onRemove }) => {
  if (!attachment) {
    return null;
  }

  const status = statusLine(attachment);
  const busy = attachment.status === 'processing';

  return (
    <div className="group relative flex items-center gap-2.5 w-[218px] p-2 pr-6 bg-dunea-elements-background-depth-3 border border-dunea-elements-borderColor rounded-lg">
      <div className="relative flex-shrink-0 w-10 h-10 rounded-md overflow-hidden bg-sunken border border-hairline flex items-center justify-center">
        <span className="i-ph:file-text text-[16px] text-muted" />
        {busy && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="i-ph:spinner-gap text-[14px] text-white ui-spin" />
          </div>
        )}
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        <span className="font-geist text-[10px] font-medium uppercase tracking-wide text-muted">PDF attachment</span>
        <span className="font-geist text-[12px] text-primary truncate" title={attachment.file.name}>
          {attachment.file.name}
        </span>
        <span
          className={classNames(
            'font-geist text-[11px] truncate',
            status.tone === 'accent' && 'text-accent',
            status.tone === 'danger' && 'text-danger',
            status.tone === 'muted' && 'text-muted',
          )}
        >
          {status.text}
        </span>
      </div>

      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        className="absolute top-1 right-1 z-10 w-5 h-5 rounded-full bg-black border border-hairline flex items-center justify-center hover:bg-gray-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed touch:w-7 touch:h-7 touch:top-0.5 touch:right-0.5"
        aria-label="Remove PDF attachment"
      >
        <span className="i-ph:x w-3 h-3 text-gray-200" />
      </button>
    </div>
  );
};

export default PdfAttachmentPreview;
